/**
 * The failure taxonomy the data layer is built on.
 *
 * Nothing here carries a role and nothing infers one: authorisation is a
 * server decision (Spec §3, `src/lib/auth.ts`), so a 401 and a 403 stay two
 * different facts rather than being folded into one "not allowed" kind — the
 * UI has to tell "sign in again" apart from "you don't hold the grant".
 *
 * `toFailure` maps an HTTP status and a parsed response body onto one member
 * of `ApiFailure`, checked against the running API (`api/src/socialsrc_api/
 * routers/catalogues.py`, `.../tracking.py`, `.../schemas/estimates.py`).
 */

import { defineCatalogue } from '../../i18n';

/** The API sends no error codes and writes every message in English. Pydantic's own
 *  `msg` ("Value error, ...", "Field required", "Input should be ...") is a developer
 *  string, so it is replaced by `rejected` unless it is already Portuguese; the domain
 *  refusals below, which a reader can act on, are translated rather than dropped. */
const errorCopy = defineCatalogue(
  {
    rejected: 'Esta alteração tem um valor que o servidor não aceita. Revise e tente de novo.',
    'duplicate.team': 'Já existe uma equipe chamada "{name}" neste nível.',
    'duplicate.category': 'Já existe uma categoria chamada "{name}".',
    'period.inverted': 'O fim não pode ser antes do início.',
    'candidate.office': 'Escolha um cargo da lista do TSE.',
    'candidate.digits': 'O número para este cargo tem {digits} dígitos.',
    'candidate.numberNeedsOffice': 'Escolha o cargo antes do número.',
    'candidate.registration': 'Esta candidatura não está mais no cadastro do TSE.',
    'candidate.uf': 'Escolha um estado da lista.',
  },
  {
    rejected: 'This change has a value the server does not accept. Review it and try again.',
    'duplicate.team': 'A team named "{name}" already exists at this level.',
    'duplicate.category': 'A category named "{name}" already exists.',
    'period.inverted': 'The end cannot be before the start.',
    'candidate.office': 'Choose an office from the TSE list.',
    'candidate.digits': 'A number for this office has {digits} digits.',
    'candidate.numberNeedsOffice': 'Choose the office before the number.',
    'candidate.registration': 'This candidature is no longer in the TSE registry.',
    'candidate.uf': 'Choose a state from the list.',
  },
).t;

/** The API's own English refusals (`core`'s `teams`/`tags` actions, `tenants.create_campaign`,
 *  `api`'s `routers/campaigns.py`), matched on their wording since no code accompanies them. */
const DOMAIN_MESSAGES: ReadonlyArray<{
  pattern: RegExp;
  copy: (match: RegExpMatchArray) => string;
}> = [
  {
    pattern: /^a team named ['"](.+)['"] already exists at this level$/,
    copy: (m) => errorCopy('duplicate.team', { name: m[1]! }),
  },
  {
    pattern: /^a category named ['"](.+)['"] already exists$/,
    copy: (m) => errorCopy('duplicate.category', { name: m[1]! }),
  },
  {
    pattern: /^active_(to must not be before active_from|from must not be after active_to)$/,
    copy: () => errorCopy('period.inverted'),
  },
  { pattern: /^not a TSE office: /, copy: () => errorCopy('candidate.office') },
  {
    pattern: /^a .+ number has (\d) digits$/,
    copy: (m) => errorCopy('candidate.digits', { digits: m[1]! }),
  },
  {
    pattern: /^a candidate number needs an office$/,
    copy: () => errorCopy('candidate.numberNeedsOffice'),
  },
  { pattern: /^no such TSE registration: /, copy: () => errorCopy('candidate.registration') },
  { pattern: /^unknown UF: /, copy: () => errorCopy('candidate.uf') },
];

function translatedDomainMessage(message: string): string | null {
  for (const { pattern, copy } of DOMAIN_MESSAGES) {
    const match = message.match(pattern);
    if (match !== null) return copy(match);
  }
  return null;
}

// ponytail: a diacritic or a common Portuguese word marks text as already Portuguese; an error
// code on the wire would replace this guess.
const PORTUGUESE = /[ãõçáéíóúâêôà]|\b(não|já|está|nome|uma?|de|da|em|para|com)\b/i;

/** A Pydantic `msg`: a validator's `ValueError` arrives prefixed with "Value error, ". */
function validationMessage(msg: unknown): string {
  if (typeof msg !== 'string') return errorCopy('rejected');
  const text = msg.replace(/^Value error, /, '');
  return translatedDomainMessage(text) ?? (PORTUGUESE.test(text) ? text : errorCopy('rejected'));
}

function domainMessage(message: string): string {
  return translatedDomainMessage(message) ?? message;
}

/** §6.3.6: "Deleting a preset that is referenced is refused; the response
 * lists the referrers." The list is navigable in the UI, so it is parsed,
 * not a string.
 *
 * The running API's `Referrers` schema (`api/src/socialsrc_api/schemas/
 * tracking.py`) returns a `campaigns` group too: `_profile_referrers` in
 * `catalogues.py` populates it when a monitoring profile is deleted while a
 * campaign still resolves to it. Dropping that group would silently discard
 * a referrer §6.3.6 requires the tenant to see.
 */
export interface Referrer {
  kind: 'profile' | 'account' | 'campaign';
  id: string;
  label: string;
}

export type ApiFailure =
  | { kind: 'unauthenticated' } // 401
  | { kind: 'forbidden'; message: string } // 403
  | { kind: 'not-found' } // 404
  | {
      kind: 'conflict';
      message: string;
      referrers: Referrer[];
      /** Set only by `POST /tenants/{tenant_id}/campaigns`'s slot gate (`api`'s
       *  `_no_slot_available`); every other conflict leaves it unset. */
      noFreeSlot?: { campaignSlots: number | null; campaignsInUse: number };
      /** Set only by a profile delete-with-reassign that would pause or resume accounts
       *  without `confirm_collection_change`. */
      collectionChange?: { change: 'resume' | 'pause'; accounts: number };
    } // 409, §6.3.6
  | { kind: 'invalid'; field: string | null; message: string } // 422
  | { kind: 'server'; status: number }
  | { kind: 'offline' }
  | { kind: 'malformed'; issues: string[] }; // Zod rejected it

function describe(failure: ApiFailure): string {
  switch (failure.kind) {
    case 'unauthenticated':
      return 'not signed in';
    case 'forbidden':
      return `forbidden: ${failure.message}`;
    case 'not-found':
      return 'not found';
    case 'conflict':
      return `conflict: ${failure.message}`;
    case 'invalid':
      return failure.field ? `invalid ${failure.field}: ${failure.message}` : failure.message;
    case 'server':
      return `server error (${failure.status})`;
    case 'offline':
      return 'offline';
    case 'malformed':
      return `malformed response: ${failure.issues.join('; ')}`;
  }
}

/** Thrown by `ApiClient`'s parsed methods in place of a bare `ApiError` —
 * `.failure` is the taxonomy member a caller switches on. */
export class ApiFailureError extends Error {
  readonly failure: ApiFailure;

  constructor(failure: ApiFailure) {
    super(describe(failure));
    this.name = 'ApiFailureError';
    this.failure = failure;
  }
}

/** FastAPI wraps every `HTTPException` body in `{"detail": ...}`
 * (`api/src/socialsrc_api/deps.py`, `routers/catalogues.py`). `detail` itself
 * is a plain string for most handlers, or a small object for the ones that
 * need structure (a 409's `{message, referrers}`, a domain 422's
 * `{field, message}`). */
function detailOf(body: unknown): unknown {
  if (body !== null && typeof body === 'object' && 'detail' in body) {
    return (body as { detail: unknown }).detail;
  }
  return body;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function messageOf(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail;
  if (isRecord(detail) && typeof detail.message === 'string') return detail.message;
  return fallback;
}

/** One string list per referrer kind — `Referrers` in
 * `api/src/socialsrc_api/schemas/tracking.py` — never a flat
 * `{kind, id, label}` row. Each string is a slug (`_slugs_by_id`/
 * `_account_referrers` in `routers/catalogues.py`), and the API sends no
 * separate id, so slug fills both `id` and `label` here. */
const REFERRER_GROUPS: ReadonlyArray<{ field: string; kind: Referrer['kind'] }> = [
  { field: 'profiles', kind: 'profile' },
  { field: 'accounts', kind: 'account' },
  { field: 'campaigns', kind: 'campaign' },
];

const REFERRER_KINDS: ReadonlySet<string> = new Set(REFERRER_GROUPS.map((group) => group.kind));

function isReferrerRow(value: unknown): value is Referrer {
  return (
    isRecord(value) &&
    typeof value.kind === 'string' &&
    REFERRER_KINDS.has(value.kind) &&
    typeof value.id === 'string' &&
    typeof value.label === 'string'
  );
}

/** Never invents a referrer list the server did not send: an unrecognised or
 * absent `referrers` value maps to `[]`, not to a guess. */
function toReferrers(value: unknown): Referrer[] {
  if (Array.isArray(value)) {
    // Already a flat {kind, id, label} row.
    return value.filter(isReferrerRow);
  }
  if (isRecord(value)) {
    // The real API's grouped shape.
    const referrers: Referrer[] = [];
    for (const { field, kind } of REFERRER_GROUPS) {
      const group = value[field];
      if (!Array.isArray(group)) continue;
      for (const slug of group) {
        if (typeof slug === 'string') referrers.push({ kind, id: slug, label: slug });
      }
    }
    return referrers;
  }
  return [];
}

function toNoFreeSlot(
  detail: Record<string, unknown>,
): { campaignSlots: number | null; campaignsInUse: number } | undefined {
  const slots = detail.campaign_slots;
  const inUse = detail.campaigns_in_use;
  if ((typeof slots !== 'number' && slots !== null) || typeof inUse !== 'number') return undefined;
  return { campaignSlots: slots, campaignsInUse: inUse };
}

function toCollectionChange(
  detail: Record<string, unknown>,
): { change: 'resume' | 'pause'; accounts: number } | undefined {
  const { collection_change: change, accounts } = detail;
  if ((change !== 'resume' && change !== 'pause') || typeof accounts !== 'number') {
    return undefined;
  }
  return { change, accounts };
}

function toConflict(body: unknown): ApiFailure {
  const detail = detailOf(body);
  return {
    kind: 'conflict',
    message: messageOf(detail, 'conflict'),
    referrers: isRecord(detail) ? toReferrers(detail.referrers) : [],
    noFreeSlot: isRecord(detail) ? toNoFreeSlot(detail) : undefined,
    collectionChange: isRecord(detail) ? toCollectionChange(detail) : undefined,
  };
}

/** A 422 body takes one of three shapes: `{"detail": {"field": ...}}`, what
 * `_validation_error_to_422` in `routers/catalogues.py` sends for a domain
 * validation failure; FastAPI's own `RequestValidationError` (raised before
 * a route body is even reached, e.g. a field of the wrong JSON type), which
 * sends `{"detail": [{"loc": [...], "msg": ..., "type": ...}, ...]}` instead
 * — an array, not an object, and `msg`/`loc` rather than `message`/`field`;
 * or a plain string `detail` (`f"no such tier: {id}"`, common in
 * `routers/tracking.py`), with no field to focus. All three are handled
 * below. */
const LOC_SOURCE_SEGMENTS = new Set(['body', 'query', 'path', 'header', 'cookie']);

function fieldFromLoc(loc: unknown[]): string | null {
  const segments = loc
    .filter((segment) => !(typeof segment === 'string' && LOC_SOURCE_SEGMENTS.has(segment)))
    .map((segment) => String(segment));
  return segments.length > 0 ? segments.join('.') : null;
}

function toInvalid(body: unknown): ApiFailure {
  const detail = detailOf(body);

  if (Array.isArray(detail)) {
    // FastAPI's own RequestValidationError shape.
    const first: unknown = detail[0];
    const loc = isRecord(first) && Array.isArray(first.loc) ? first.loc : [];
    return {
      kind: 'invalid',
      field: fieldFromLoc(loc),
      message: validationMessage(isRecord(first) ? first.msg : undefined),
    };
  }

  if (isRecord(detail)) {
    // The domain shape: `_validation_error_to_422` in routers/catalogues.py.
    // The fallback is prose, not the bare field name: `_validation_error_to_422`
    // always sends a `message` alongside `field`, but nothing guarantees that of
    // every 422 producer, and a raw field name ("interval_seconds") rendered as
    // the failure text reads as a broken label, not a sentence a tenant reads.
    const field = typeof detail.field === 'string' ? detail.field : null;
    const fallback = field ? `${field} is invalid` : 'the request was invalid';
    return { kind: 'invalid', field, message: domainMessage(messageOf(detail, fallback)) };
  }

  if (typeof detail === 'string') {
    return { kind: 'invalid', field: null, message: domainMessage(detail) };
  }

  return { kind: 'invalid', field: null, message: 'invalid' };
}

/** Maps an HTTP status and a parsed (or unparseable) response body onto the
 * failure taxonomy. Called with the body already run through `JSON.parse` —
 * or `undefined`/the raw text when it was not JSON. */
export function toFailure(status: number, body: unknown): ApiFailure {
  switch (status) {
    case 401:
      return { kind: 'unauthenticated' };
    case 403:
      return { kind: 'forbidden', message: messageOf(detailOf(body), 'forbidden') };
    case 404:
      return { kind: 'not-found' };
    case 409:
      return toConflict(body);
    case 422:
      return toInvalid(body);
    default:
      // Every other status, 4xx included (429 rate-limited, 400 bad
      // request, ...), lands on {kind:'server'} too, which reads to the
      // tenant as a server fault rather than a client-caused one. Kept
      // deliberately rather than widened: no route in
      // `api/src/socialsrc_api` raises a 429 today, and `ApiFailure` gains
      // a member when a real route needs one distinguished, not ahead of
      // that need.
      return { kind: 'server', status };
  }
}
